// blog-posts/post-market-making-games.js
export default {
  id: 2,
  title: "Market Making Games: How Traders Learn to Think in Spreads",
  date: "2026-05-10",
  preview: "Market making games are a staple of trading interviews — deceptively simple on the surface, but packed with real trading concepts. Learn how to calculate fair value, manage inventory, and read your counterparty using a single unusual die.",
  content: `
    <style>
      .post-section p {
        margin-bottom: 15px;
      }
      .post-section h2 {
        margin-top: 30px;
        margin-bottom: 10px;
      }
      .post-section table {
        border-collapse: collapse;
        margin: 20px 0;
        width: 100%;
        max-width: 400px;
      }
      .post-section table th,
      .post-section table td {
        border: 1px solid #ccc;
        padding: 8px 14px;
        text-align: center;
      }
      .post-section table th {
        background-color: #f4f4f4;
        font-weight: bold;
      }
      .post-section .quote-block {
        background: #f9f9f9;
        border-left: 4px solid #ccc;
        padding: 12px 18px;
        margin: 20px 0;
        font-style: italic;
      }
      .post-section hr {
        border: none;
        border-top: 1px solid #e0e0e0;
        margin: 30px 0;
      }
    </style>
    <div class="post-section">
      <p>If you've ever sat through a trading interview or attended a trading firm open day, you've almost certainly encountered a market making game. They're deceptively simple on the surface — someone rolls a die, and you have to make a two-sided market on the outcome. But beneath that simplicity lies everything that matters in real trading: probability, risk management, reading your counterparty, and staying sharp under pressure.</p>

      <hr>

      <h2>What Is a Market Making Game?</h2>
      <p>A market making game simulates the core job of a market maker: continuously quoting a <strong>bid</strong> (the price you'll buy at) and an <strong>offer</strong> (the price you'll sell at). Your counterparty — another player or an interviewer — can then choose to buy from you at your offer, sell to you at your bid, or pass.</p>
      <p>You profit from the <strong>spread</strong> (the gap between your bid and offer), but you bear the <strong>inventory risk</strong> of being wrong about the fair value. Quote too wide and nobody trades with you. Quote too tight and a well-informed counterparty will pick you off.</p>

      <hr>

      <b><h2>The Die: A Twist on the Classic</h2></b>
      <p>Let's not use a standard six-sided die. Instead, imagine a die with the following faces:</p>
      <div class="quote-block">1, 1, 2, 3, 4, 7</div>
      <p>Two faces show a 1, the numbers 5 and 6 are missing entirely, and there's a 7. It looks strange — and that's exactly the point.</p>

      <h2>What's the Fair Value?</h2>
      <p>The expected value (EV) of a roll is simply the average outcome across all equally likely faces:</p>
      <div class="quote-block">EV = (1 + 1 + 2 + 3 + 4 + 7) / 6 = 18 / 6 = 3.00</div>
      <p>Compare this to a standard die:</p>
      <div class="quote-block">EV (standard) = (1 + 2 + 3 + 4 + 5 + 6) / 6 = 21 / 6 = 3.50</div>
      <p>This die has a fair value of exactly <strong>3.00</strong>. Someone who assumes they're playing with a standard die and quotes around 3.50 is already half a point off before the game even begins. This is your first lesson: <strong>know your instrument</strong>. In real markets, traders who understand the underlying asset better than their counterparty have a structural edge before a single trade is made.</p>

      <hr>

      <h2>Making Your First Market</h2>
      <p>Suppose the game master asks you to make a market on the roll of this die. You know the fair value is exactly 3.00. A reasonable starting market might be:</p>
      <div class="quote-block">Bid: 2.75 &nbsp;/&nbsp; Offer: 3.25</div>
      <p>This gives you a spread of 0.50, sitting symmetrically ±0.25 on each side of fair value. If someone buys at 3.25, you profit whenever the roll comes in below 3.25 — which happens four out of six times (outcomes 1, 1, 2, or 3). If someone sells at 2.75, you profit whenever the roll comes in above 2.75 — outcomes 3, 4, or 7.</p>
      <p>But here's where it gets interesting. What would your market be?</p>

      <hr>

      <h2>Reading Your Counterparty</h2>
      <p>The moment someone trades with you, you gain information. Watch these signals:</p>
      <p><strong>They buy your offer immediately, without hesitation.</strong> This suggests they think the fair value is <em>higher</em> than your offer. Should you revise upward? Maybe — or maybe they're bluffing.</p>
      <p><strong>They walk away without trading.</strong> Your spread isn't crossing anyone's threshold. Either your bid is too low, your offer too high, or both — nobody thinks there's value on either side. Consider tightening your market.</p>
      <p><strong>They keep selling your bid.</strong> They think fair value is below your bid. Consider tightening or lowering your market.</p>
      <p>In a game with multiple rounds, the pattern of who is buying versus selling tells you whether you've mispriced your market — and by how much.</p>

      <hr>

      <h2>The Inventory Problem</h2>
      <p>Suppose five players in a row all <strong>buy</strong> from you at your offer of 3.25. You've now sold five units of the die roll. You're short five times the outcome. Even if fair value is 3.00, variance is your enemy at this point — a single roll of 7 hurts badly when you're short.</p>
      <p>A real market maker would respond by <strong>adjusting their quotes</strong>. After building up short inventory, you might quote:</p>
      <div class="quote-block">Bid: 3.00 &nbsp;/&nbsp; Offer: 4.00</div>
      <p>Now you're more eager to buy (tighter bid) and more reluctant to sell (wider offer on the upside). This reflects your inventory position, not just fair value. Managing the tension between fair value and inventory is the central skill of market making — in games and in real life.</p>

      <hr>

      <h2>The 1,1,2,3,4,7 Effect: Distribution Matters</h2>
      <p>This die has some interesting properties worth thinking through carefully. The probability distribution looks like this:</p>
      <table>
        <thead>
          <tr><th>Outcome</th><th>Probability</th></tr>
        </thead>
        <tbody>
          <tr><td>1</td><td>2/6 ≈ 33.3%</td></tr>
          <tr><td>2</td><td>1/6 ≈ 16.7%</td></tr>
          <tr><td>3</td><td>1/6 ≈ 16.7%</td></tr>
          <tr><td>4</td><td>1/6 ≈ 16.7%</td></tr>
          <tr><td>7</td><td>1/6 ≈ 16.7%</td></tr>
        </tbody>
      </table>
      <p>The <strong>mode</strong> is 1 — it's the most likely single outcome. The <strong>median</strong> is 2.5. But the <strong>mean</strong> is 3.00. These are all very different numbers, and they matter in different contexts.</p>
      <p>Notice the 7 on the die. It only comes up one sixth of the time, but it single-handedly pulls the mean up from what would otherwise be around 2.20 to exactly 3.00. This is a great illustration of how a rare but extreme outcome can dominate the expected value — a concept that shows up constantly in trading when dealing with tail risk, options pricing, and skewed payoff distributions.</p>
      <p>If someone asks you to make a market on whether the roll lands <strong>above or below 3</strong>, the answer isn't obvious from the mean alone. Outcomes strictly below 3 (rolling a 1 or 2) happen with probability 3/6 = 50%. Outcomes strictly above 3 (rolling a 4 or 7) happen with probability 2/6 ≈ 33%. Rolling exactly 3 is another 1/6 ≈ 17%. A clever game master could use this to probe whether you're actually thinking through the distribution or just anchoring on the EV.</p>

      <hr>

      <h2>Common Mistakes to Avoid</h2>
      <p><strong>1. Anchoring too hard on EV.</strong> Expected value is a starting point, not the whole story. Distribution shape, skew, and the number of rounds all matter.</p>
      <p><strong>2. Keeping your spread too wide.</strong> Nobody will trade with you, and in a competitive game, a tighter competitor will capture all the flow and gain all the information.</p>
      <p><strong>3. Keeping your spread too tight.</strong> You'll get picked off the moment someone has better information than you — or just gets lucky.</p>
      <p><strong>4. Ignoring inventory.</strong> Many beginners quote the same market all game regardless of their position. Real market makers are constantly adjusting.</p>
      <p><strong>5. Tilting after a bad run.</strong> Variance is real. A few bad rolls don't mean your pricing was wrong. Stay disciplined and trust the math over small samples.</p>

      <hr>

      <h2>Practice Ideas</h2>
      <p>If you want to get better at these games, here are a few ways to train:</p>
      <p><strong>Play with friends</strong> using any unusual object as the randomizer — a spinner, a card draw, a non-standard die. The more unfamiliar the distribution, the better.</p>
      <p><strong>Add information asymmetry</strong> — one player secretly knows part of the outcome (e.g., whether the roll was high or low) and can choose to trade. The market maker has to figure out when they're being informed.</p>
      <p><strong>Track your P&amp;L</strong> over many rounds. Short samples are noisy; your edge (or lack of it) only becomes clear over time.</p>
      <p><strong>Practice with time pressure</strong> — give yourself five seconds to quote. Speed forces you to internalize the math rather than derive it fresh each time.</p>

      <hr>

      <p>The best market makers — in games and in real trading rooms — are not the ones who are always right. They're the ones who are consistently <strong>less wrong than the market</strong>, who update quickly, and who manage risk well when things go sideways. A strange little die with two 1s and a 7 is as good a place as any to start building that discipline.</p>
    </div>
  `
}